import { useState, useEffect, useMemo } from "react";
import { Plus, Pencil, Trash2, AlertTriangle, Package, Pill, Dumbbell, Sparkles, Box, Search, Filter, ShoppingCart, X, Check, FileDown, Tag, FolderPlus, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const ICON_MAP = {
  pill: Pill,
  dumbbell: Dumbbell,
  sparkles: Sparkles,
  box: Box,
  package: Package,
  tag: Tag
};

// Available colors for new categories
const AVAILABLE_COLORS = [
  { id: "bg-purple-500", name: "Lila" },
  { id: "bg-blue-500", name: "Blau" },
  { id: "bg-cyan-500", name: "Cyan" },
  { id: "bg-green-500", name: "Grün" },
  { id: "bg-yellow-500", name: "Gelb" },
  { id: "bg-orange-500", name: "Orange" },
  { id: "bg-red-500", name: "Rot" },
  { id: "bg-pink-500", name: "Pink" },
  { id: "bg-slate-500", name: "Grau" }
];

// Available icons for new categories
const AVAILABLE_ICONS = [
  { id: "box", name: "Box", Icon: Box },
  { id: "package", name: "Paket", Icon: Package },
  { id: "tag", name: "Tag", Icon: Tag },
  { id: "pill", name: "Pille", Icon: Pill },
  { id: "dumbbell", name: "Hantel", Icon: Dumbbell },
  { id: "sparkles", name: "Sterne", Icon: Sparkles }
];

export default function InventoryPage({ onLogout }) {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterLowStock, setFilterLowStock] = useState(false);
  
  // Product modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "Outros",
    photo_url: "",
    stock_target: 10,
    stock_current: 0
  });
  
  // Category modal
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({
    name: "",
    icon: "box",
    color: "bg-slate-500"
  });
  
  // Auto-populate modal
  const [isAutoPopulateModalOpen, setIsAutoPopulateModalOpen] = useState(false);
  const [autoPopulateLoading, setAutoPopulateLoading] = useState(false);

  useEffect(() => {
    loadCategories();
    loadInventory();
    loadStats();
  }, []);

  const loadCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/inventory/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Error loading categories:", error);
      // Fallback to default categories
      setCategories([
        { id: "Suplementos", name: "Suplementos", icon: "pill", color: "bg-purple-500", is_default: true },
        { id: "Equipamentos", name: "Equipamentos", icon: "dumbbell", color: "bg-blue-500", is_default: true },
        { id: "Higiene", name: "Higiene", icon: "sparkles", color: "bg-cyan-500", is_default: true },
        { id: "Outros", name: "Outros", icon: "box", color: "bg-slate-500", is_default: true }
      ]);
    }
  };

  const loadInventory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data.items || []);
    } catch (error) {
      console.error("Error loading inventory:", error);
      toast.error("Fehler beim Laden des Lagerbestands");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/inventory/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (item.description || "").toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === "all" || item.category === filterCategory;
      const matchesLowStock = !filterLowStock || item.stock_to_buy > 0;
      return matchesSearch && matchesCategory && matchesLowStock;
    });
  }, [items, searchTerm, filterCategory, filterLowStock]);

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        description: item.description || "",
        category: item.category,
        photo_url: item.photo_url || "",
        stock_target: item.stock_target,
        stock_current: item.stock_current
      });
    } else {
      setEditingItem(null);
      setFormData({
        name: "",
        description: "",
        category: categories.length > 0 ? categories[0].name : "Outros",
        photo_url: "",
        stock_target: 10,
        stock_current: 0
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Bitte geben Sie einen Produktnamen ein");
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      if (editingItem) {
        await axios.put(`${API}/inventory/${editingItem.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Produkt aktualisiert");
      } else {
        await axios.post(`${API}/inventory`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Produkt hinzugefügt");
      }
      
      closeModal();
      loadInventory();
      loadStats();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Möchten Sie "${item.name}" wirklich löschen?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/inventory/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Produkt gelöscht");
      loadInventory();
      loadStats();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const quickUpdateStock = async (item, newCurrent) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/inventory/${item.id}`, {
        stock_current: Math.max(0, newCurrent)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadInventory();
      loadStats();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const downloadPDF = () => {
    const token = localStorage.getItem('token');
    fetch(`${API}/inventory/pdf`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `lagerbestand_${new Date().toISOString().slice(0, 10)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("PDF heruntergeladen");
      })
      .catch(() => {
        toast.error("Fehler beim Herunterladen des PDFs");
      });
  };

  // Category functions
  const openCategoryModal = () => {
    setCategoryFormData({ name: "", icon: "box", color: "bg-slate-500" });
    setIsCategoryModalOpen(true);
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    
    if (!categoryFormData.name.trim()) {
      toast.error("Bitte geben Sie einen Kategorienamen ein");
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/inventory/categories`, categoryFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Kategorie erstellt");
      setIsCategoryModalOpen(false);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen der Kategorie");
    }
  };

  const handleDeleteCategory = async (category) => {
    if (category.is_default) {
      toast.error("Standardkategorien können nicht gelöscht werden");
      return;
    }
    
    if (!window.confirm(`Möchten Sie die Kategorie "${category.name}" wirklich löschen?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/inventory/categories/${category.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Kategorie gelöscht");
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen der Kategorie");
    }
  };

  const handleAutoPopulate = async (force = false) => {
    setAutoPopulateLoading(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = force ? `${API}/inventory/auto-populate/force` : `${API}/inventory/auto-populate`;
      
      const response = await axios.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setIsAutoPopulateModalOpen(false);
      loadInventory();
      loadStats();
    } catch (error) {
      if (error.response?.status === 400 && !force) {
        // User has existing items, show confirmation modal
        setIsAutoPopulateModalOpen(true);
      } else {
        toast.error(error.response?.data?.detail || "Fehler beim Hinzufügen der Produkte");
      }
    } finally {
      setAutoPopulateLoading(false);
    }
  };

  const getCategoryInfo = (categoryName) => {
    const cat = categories.find(c => c.name === categoryName || c.id === categoryName);
    if (cat) {
      const IconComponent = ICON_MAP[cat.icon] || Box;
      return { ...cat, IconComponent };
    }
    return { name: categoryName, icon: "box", color: "bg-slate-500", IconComponent: Box };
  };

  const getStockStatus = (item) => {
    const percentage = (item.stock_current / item.stock_target) * 100;
    if (percentage <= 0) return { color: "bg-red-500", text: "Ausverkauft", alert: true };
    if (percentage <= 25) return { color: "bg-red-400", text: "Kritisch", alert: true };
    if (percentage <= 50) return { color: "bg-amber-400", text: "Niedrig", alert: true };
    if (percentage <= 75) return { color: "bg-yellow-400", text: "Mittel", alert: false };
    return { color: "bg-green-500", text: "OK", alert: false };
  };

  return (
    <Layout onLogout={onLogout}>
      <div className="p-4 md:p-6 h-full flex flex-col overflow-hidden" data-testid="inventory-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t("inventory_title")}
            </h1>
            <p className="text-slate-600 text-sm mt-1">{t("inventory_subtitle")}</p>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => handleAutoPopulate(false)} data-testid="auto-populate-button" disabled={autoPopulateLoading}>
              <Wand2 className="w-4 h-4 mr-2" />
              {autoPopulateLoading ? "Laden..." : "Auto-Füllen"}
            </Button>
            <Button variant="outline" size="sm" onClick={openCategoryModal} data-testid="manage-categories-button">
              <FolderPlus className="w-4 h-4 mr-2" />
              Kategorie
            </Button>
            <Button variant="outline" onClick={downloadPDF} data-testid="download-pdf-button">
              <FileDown className="w-4 h-4 mr-2" />
              PDF
            </Button>
            <Button onClick={() => openModal()} data-testid="add-product-button">
              <Plus className="w-4 h-4 mr-2" />
              Produkt
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-slate-500" />
                  <div>
                    <p className="text-xs text-slate-500">Gesamt</p>
                    <p className="text-lg font-bold text-slate-900">{stats.total_items}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className={`${stats.low_stock_items > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className={`w-5 h-5 ${stats.low_stock_items > 0 ? 'text-amber-500' : 'text-green-500'}`} />
                  <div>
                    <p className="text-xs text-slate-500">Niedrig</p>
                    <p className={`text-lg font-bold ${stats.low_stock_items > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                      {stats.low_stock_items}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className={`${stats.out_of_stock > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <X className={`w-5 h-5 ${stats.out_of_stock > 0 ? 'text-red-500' : 'text-green-500'}`} />
                  <div>
                    <p className="text-xs text-slate-500">Ausverkauft</p>
                    <p className={`text-lg font-bold ${stats.out_of_stock > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {stats.out_of_stock}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="text-xs text-slate-500">Zu kaufen</p>
                    <p className="text-lg font-bold text-blue-600">{stats.total_to_buy}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Categories Bar */}
        <div className="flex flex-wrap gap-2 mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <span className="text-xs font-medium text-slate-500 self-center mr-2">Kategorien:</span>
          {categories.map(cat => {
            const IconComponent = ICON_MAP[cat.icon] || Box;
            return (
              <div key={cat.id} className="flex items-center gap-1">
                <Badge 
                  variant="secondary" 
                  className={`${cat.color} text-white cursor-pointer hover:opacity-80`}
                  onClick={() => setFilterCategory(filterCategory === cat.name ? "all" : cat.name)}
                >
                  <IconComponent className="w-3 h-3 mr-1" />
                  {cat.name}
                </Badge>
                {!cat.is_default && (
                  <button
                    onClick={() => handleDeleteCategory(cat)}
                    className="text-slate-400 hover:text-red-500 transition-colors"
                    title="Kategorie löschen"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={openCategoryModal}
          >
            <Plus className="w-3 h-3 mr-1" />
            Neu
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Produkt suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
          
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[160px]" data-testid="category-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Kategorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Kategorien</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            variant={filterLowStock ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterLowStock(!filterLowStock)}
            className={filterLowStock ? "bg-amber-500 hover:bg-amber-600" : ""}
            data-testid="low-stock-filter"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Nur niedrig
          </Button>
        </div>

        {/* Inventory Table */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm flex-1 overflow-hidden">
          <div className="overflow-auto h-full">
            <table className="w-full">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Foto</th>
                  <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Produkt</th>
                  <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase tracking-wider hidden md:table-cell">Kategorie</th>
                  <th className="text-center p-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Soll</th>
                  <th className="text-center p-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Ist</th>
                  <th className="text-center p-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Kaufen</th>
                  <th className="text-center p-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-right p-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-slate-500">
                      Laden...
                    </td>
                  </tr>
                ) : filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-slate-500">
                      {items.length === 0 ? (
                        <div className="flex flex-col items-center">
                          <Package className="w-12 h-12 text-slate-300 mb-2" />
                          <p>Keine Produkte vorhanden</p>
                          <Button
                            variant="link"
                            onClick={() => openModal()}
                            className="mt-2"
                          >
                            Erstes Produkt hinzufügen
                          </Button>
                        </div>
                      ) : (
                        "Keine Produkte gefunden"
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => {
                    const category = getCategoryInfo(item.category);
                    const CategoryIcon = category.IconComponent;
                    const status = getStockStatus(item);
                    
                    return (
                      <tr 
                        key={item.id} 
                        className={`hover:bg-slate-50 ${status.alert ? 'bg-red-50/30' : ''}`}
                        data-testid={`inventory-row-${item.id}`}
                      >
                        {/* Photo */}
                        <td className="p-3">
                          {item.photo_url ? (
                            <img 
                              src={item.photo_url} 
                              alt={item.name}
                              className="w-12 h-12 object-cover rounded-lg border border-slate-200"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24' fill='none' stroke='%23cbd5e1' stroke-width='1' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'%3E%3C/circle%3E%3Cpolyline points='21 15 16 10 5 21'%3E%3C/polyline%3E%3C/svg%3E";
                              }}
                            />
                          ) : (
                            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                              <CategoryIcon className="w-6 h-6 text-slate-400" />
                            </div>
                          )}
                        </td>
                        
                        {/* Product Name & Description */}
                        <td className="p-3">
                          <div className="font-medium text-slate-900">{item.name}</div>
                          {item.description && (
                            <div className="text-xs text-slate-500 truncate max-w-[200px]">{item.description}</div>
                          )}
                        </td>
                        
                        {/* Category */}
                        <td className="p-3 hidden md:table-cell">
                          <Badge variant="secondary" className={`${category.color} text-white`}>
                            <CategoryIcon className="w-3 h-3 mr-1" />
                            {category.name}
                          </Badge>
                        </td>
                        
                        {/* Stock Target */}
                        <td className="p-3 text-center">
                          <span className="text-slate-600 font-medium">{item.stock_target}</span>
                        </td>
                        
                        {/* Stock Current - Editable */}
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => quickUpdateStock(item, item.stock_current - 1)}
                            >
                              -
                            </Button>
                            <span className={`font-bold min-w-[24px] ${item.stock_current === 0 ? 'text-red-600' : 'text-slate-900'}`}>
                              {item.stock_current}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => quickUpdateStock(item, item.stock_current + 1)}
                            >
                              +
                            </Button>
                          </div>
                        </td>
                        
                        {/* Stock To Buy */}
                        <td className="p-3 text-center">
                          <span className={`font-bold ${item.stock_to_buy > 0 ? 'text-blue-600' : 'text-green-600'}`}>
                            {item.stock_to_buy > 0 ? item.stock_to_buy : <Check className="w-4 h-4 mx-auto text-green-500" />}
                          </span>
                        </td>
                        
                        {/* Status */}
                        <td className="p-3 text-center">
                          <Badge className={`${status.color} text-white`}>
                            {status.text}
                          </Badge>
                        </td>
                        
                        {/* Actions */}
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openModal(item)}
                              data-testid={`edit-${item.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(item)}
                              data-testid={`delete-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add/Edit Product Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? "Produkt bearbeiten" : "Neues Produkt"}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Produktname"
                  data-testid="product-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="description">Beschreibung</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Kurze Beschreibung (optional)"
                  rows={2}
                />
              </div>
              
              <div>
                <Label htmlFor="category">Kategorie</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => {
                      const IconComponent = ICON_MAP[cat.icon] || Box;
                      return (
                        <SelectItem key={cat.id} value={cat.name}>
                          <div className="flex items-center gap-2">
                            <IconComponent className="w-4 h-4" />
                            {cat.name}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="photo_url">Foto URL</Label>
                <Input
                  id="photo_url"
                  value={formData.photo_url}
                  onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                />
                <p className="text-xs text-slate-500 mt-1">Link zu einem Bild des Produkts</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="stock_target">Soll-Bestand</Label>
                  <Input
                    id="stock_target"
                    type="number"
                    min="0"
                    value={formData.stock_target}
                    onChange={(e) => setFormData({ ...formData, stock_target: parseInt(e.target.value) || 0 })}
                    data-testid="stock-target-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="stock_current">Aktueller Bestand</Label>
                  <Input
                    id="stock_current"
                    type="number"
                    min="0"
                    value={formData.stock_current}
                    onChange={(e) => setFormData({ ...formData, stock_current: parseInt(e.target.value) || 0 })}
                    data-testid="stock-current-input"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeModal}>
                  Abbrechen
                </Button>
                <Button type="submit" data-testid="save-product-button">
                  {editingItem ? "Speichern" : "Hinzufügen"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Category Modal */}
        <Dialog open={isCategoryModalOpen} onOpenChange={setIsCategoryModalOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Neue Kategorie</DialogTitle>
              <DialogDescription>
                Erstellen Sie eine neue Kategorie für Ihre Produkte
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleCreateCategory} className="space-y-4">
              <div>
                <Label htmlFor="cat-name">Name *</Label>
                <Input
                  id="cat-name"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  placeholder="Kategoriename"
                  data-testid="category-name-input"
                />
              </div>
              
              <div>
                <Label>Symbol</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {AVAILABLE_ICONS.map(icon => {
                    const IconComponent = icon.Icon;
                    return (
                      <button
                        key={icon.id}
                        type="button"
                        onClick={() => setCategoryFormData({ ...categoryFormData, icon: icon.id })}
                        className={`p-2 rounded-lg border-2 transition-colors ${
                          categoryFormData.icon === icon.id 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        title={icon.name}
                      >
                        <IconComponent className="w-5 h-5" />
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <div>
                <Label>Farbe</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {AVAILABLE_COLORS.map(color => (
                    <button
                      key={color.id}
                      type="button"
                      onClick={() => setCategoryFormData({ ...categoryFormData, color: color.id })}
                      className={`w-8 h-8 rounded-full ${color.id} ${
                        categoryFormData.color === color.id 
                          ? 'ring-2 ring-offset-2 ring-blue-500' 
                          : ''
                      }`}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>
              
              {/* Preview */}
              <div className="pt-2 border-t">
                <Label className="text-xs text-slate-500">Vorschau</Label>
                <div className="mt-2">
                  <Badge className={`${categoryFormData.color} text-white`}>
                    {(() => {
                      const IconComponent = ICON_MAP[categoryFormData.icon] || Box;
                      return <IconComponent className="w-3 h-3 mr-1" />;
                    })()}
                    {categoryFormData.name || "Kategoriename"}
                  </Badge>
                </div>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCategoryModalOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" data-testid="save-category-button">
                  Erstellen
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Auto-Populate Confirmation Modal */}
        <Dialog open={isAutoPopulateModalOpen} onOpenChange={setIsAutoPopulateModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-purple-500" />
                Lager automatisch füllen
              </DialogTitle>
              <DialogDescription>
                Sie haben bereits Produkte in Ihrem Lager. Möchten Sie trotzdem die Standard-Produkte für ein EMS-Studio hinzufügen?
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-medium text-purple-900 mb-2">Es werden hinzugefügt:</h4>
                <ul className="text-sm text-purple-700 space-y-1">
                  <li>• <strong>Suplementos:</strong> Proteinpulver, BCAA, Magnesium, etc.</li>
                  <li>• <strong>Equipamentos:</strong> EMS Elektroden, Gel, Anzüge, Matten</li>
                  <li>• <strong>Higiene:</strong> Desinfektionsmittel, Handtücher, etc.</li>
                  <li>• <strong>Outros:</strong> Wasserflaschen, Erste-Hilfe-Kasten, etc.</li>
                </ul>
                <p className="text-xs text-purple-600 mt-3">
                  Produkte mit dem gleichen Namen werden nicht dupliziert.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsAutoPopulateModalOpen(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={() => handleAutoPopulate(true)} 
                disabled={autoPopulateLoading}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="confirm-auto-populate-button"
              >
                {autoPopulateLoading ? "Hinzufügen..." : "Produkte hinzufügen"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
